import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Activity, User, Mail, Phone, Lock, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import apiClient from "../lib/apiClient"; 

export default function RegisterPage() {
  const [step, setStep] = useState(1); 
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ name: "", email: "", phone: "+91", password: "" });
  const [otp, setOtp] = useState("");

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === 'phone') {
        if (!value.startsWith('+91')) {
          const afterPotentialDeletion = value.replace(/\D/g, ''); 
          value = '+91' + afterPotentialDeletion;
        }
        const afterPrefixRaw = value.substring(3);
        const sanitizedAfter = afterPrefixRaw.replace(/\D/g, ''); 
        value = '+91' + sanitizedAfter;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const extractError = (err, defaultMsg) => {
    console.error("Raw Error from backend:", err); 

    if (err.response) {
        if (err.response.data?.errors && typeof err.response.data.errors === 'object') {
          const firstErrorField = Object.keys(err.response.data.errors)[0];
          return err.response.data.errors[firstErrorField][0];
        }
        if (err.response.data?.message) {
            return err.response.data.message;
        }
    }
    else if (err.request) {
        return "Network error: API server is unreachable.";
    } 
    return err.message || defaultMsg;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // 🔥 Strict Indian Mobile Number Validation 🔥
    const indianPhoneRegex = /^\+91[6-9]\d{9}$/;
    if (!indianPhoneRegex.test(formData.phone)) {
       toast.error("Please enter a valid 10-digit Indian mobile number.");
       return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post("/register", formData);
      toast.success(res.data.message);
      
      if (res.data.dev_otp) toast.info(`TEST OTP: ${res.data.dev_otp}`, { duration: 5000 }); 
      setStep(2); 
    } catch (err) {
      toast.error(extractError(err, "Registration Failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiClient.post("/verify-otp", { email: formData.email, otp });
      
      login({ email: formData.email, role: res.data.role, name: formData.name }, res.data.token);
      
      toast.success("Email verified successfully!");
      navigate("/rrg");
    } catch (err) {
      toast.error(extractError(err, "OTP Verification Failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-2xl border-0 bg-white rounded-2xl">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-sky-100 p-3 rounded-2xl mb-4">
            {step === 1 ? <Activity className="w-8 h-8 text-sky-600" /> : <ShieldCheck className="w-8 h-8 text-emerald-600" />}
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">
            {step === 1 ? "Join QuantRRG" : "Verify Email"}
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1 text-center">
            {step === 1 ? "Create an account for advanced analytics" : `Enter the 6-digit OTP sent to ${formData.email}`}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Full Name</Label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input name="name" value={formData.name} onChange={handleChange} className="pl-9 bg-slate-50" required placeholder="Your Name" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input type="email" name="email" value={formData.email} onChange={handleChange} className="pl-9 bg-slate-50" required placeholder="email@example.com" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Phone (Indian Mobile)</Label>
              <div className="relative">
                <Phone className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  className="pl-9 bg-slate-50 font-mono tracking-tight" 
                  required 
                  placeholder="+91 9876543210"
                  minLength="13" 
                  maxLength="13" 
                />
              </div>
              <p className="text-[10px] text-slate-400 font-medium pl-1">
                Format: <span className="font-bold text-slate-500">+91</span> followed by <span className="font-bold text-slate-500">10 valid digits</span>.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input type="password" name="password" value={formData.password} onChange={handleChange} className="pl-9 bg-slate-50" required minLength="6" />
              </div>
              <p className="text-[10px] text-slate-400 font-medium pl-1">
                Security: Minimum <span className="font-bold text-slate-500">6 characters</span> required.
              </p>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-11 bg-sky-600 hover:bg-sky-500 text-white font-bold shadow-md mt-4">
              {loading ? "Processing..." : "Create Account & Send OTP"}
            </Button>

            <p className="text-center text-sm font-medium text-slate-500 mt-4">
              Already have an account? <Link to="/login" className="text-sky-600 font-bold hover:underline">Login</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest text-center block">One Time Password</Label>
              <Input 
                type="text" 
                maxLength="6" 
                placeholder="000000" 
                value={otp} 
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} 
                className="h-14 text-center text-2xl tracking-[0.5em] font-black bg-slate-50 border-slate-200" 
                required 
              />
            </div>
            <Button type="submit" disabled={loading || otp.length !== 6} className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg">
              {loading ? "Verifying..." : "Verify & Login"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setStep(1)} className="w-full text-slate-500 hover:text-slate-800 text-xs font-bold">
              Change Email Address
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}